import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatRands } from "@/lib/order";
import { useCart } from "@/context/cart-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/States";
import { cn } from "@/lib/utils";

const TIPS = [0, 10, 20, 30];

export default function Checkout() {
  const cart = useCart();
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [tip, setTip] = useState(10);
  const [promo, setPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);

  const vendor = useQuery({
    queryKey: ["vendor", cart.vendorId],
    queryFn: () => api(`/vendors/${cart.vendorId}`),
    enabled: !!cart.vendorId,
  });

  const items = cart.list.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));
  const coords = vendor.data ? { lat: vendor.data.latitude, lng: vendor.data.longitude } : null;

  useEffect(() => {
    if (!coords || items.length === 0) return;
    let cancelled = false;
    api("/orders/quote", {
      method: "POST",
      json: { vendor_id: cart.vendorId, items, delivery_lat: coords.lat, delivery_lng: coords.lng, tip, promo_code: promo || null },
    })
      .then((q) => !cancelled && setQuote(q))
      .catch((e) => {
        if (!cancelled && promo) toast.error(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [vendor.data, tip, promo, cart.vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (cart.count === 0) {
    return <EmptyState title="Your cart is empty" action={<Button className="rounded-full" onClick={() => navigate("/")}>Browse stores</Button>} />;
  }

  const placeOrder = async () => {
    if (!address.trim()) {
      toast.error("Please enter a delivery address");
      return;
    }
    setPlacing(true);
    try {
      const order = await api("/orders", {
        method: "POST",
        json: {
          vendor_id: cart.vendorId,
          items,
          delivery_address: address,
          delivery_lat: coords.lat,
          delivery_lng: coords.lng,
          delivery_notes: notes || null,
          tip,
          promo_code: promo || null,
          payment_method: "mock_card",
        },
      });
      cart.clear();
      toast.success("Order placed!");
      navigate(`/order/${order.order_id}`, { replace: true });
    } catch (e) {
      toast.error(e.message || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <div className="space-y-6">
        <h1 className="font-display text-2xl">Checkout</h1>

        <Card className="rounded-2xl p-5">
          <h2 className="font-display text-lg">Delivery</h2>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="addr">Delivery address</Label>
              <Input id="addr" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="12 Dorp Street, Stellenbosch" data-testid="address-input" />
              <p className="text-xs text-muted-foreground" data-testid="address-privacy-notice">
                Your delivery address is used to fulfil your order and is shared with the delivery driver responsible for delivering it.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">Delivery notes (optional)</Label>
              <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate code, landmarks…" data-testid="notes-input" />
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl p-5">
          <h2 className="font-display text-lg">Add a tip</h2>
          <div className="mt-3 flex gap-2">
            {TIPS.map((t) => (
              <button
                key={t}
                onClick={() => setTip(t)}
                data-testid={`tip-${t}`}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm transition-colors",
                  tip === t ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
                )}
              >
                {t === 0 ? "No tip" : formatRands(t)}
              </button>
            ))}
          </div>
        </Card>

        <Card className="rounded-2xl p-5">
          <Label htmlFor="promo">Promo code</Label>
          <div className="mt-2 flex gap-2">
            <Input id="promo" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="WELCOME10" data-testid="promo-input" />
          </div>
        </Card>
      </div>

      <div>
        <Card className="sticky top-24 rounded-2xl p-5">
          <h2 className="font-display text-lg">Order summary</h2>
          <p className="mt-1 text-sm text-muted-foreground">{cart.vendorName}</p>
          <div className="mt-4 space-y-2 text-sm">
            <Row label="Subtotal" value={quote?.subtotal ?? cart.subtotal} />
            <Row label="Delivery fee" value={quote?.delivery_fee} />
            <Row label="Service fee" value={quote?.service_fee} />
            <Row label="Tip" value={quote?.tip ?? tip} />
            {quote?.discount > 0 && <Row label="Discount" value={-quote.discount} className="text-[hsl(var(--success))]" />}
          </div>
          <div className="mt-4 flex justify-between border-t pt-4">
            <span className="font-display text-base">Total</span>
            <span className="font-display text-base" data-testid="order-total">{formatRands(quote?.total ?? cart.subtotal)}</span>
          </div>
          <Button className="mt-5 w-full rounded-full" disabled={placing || !coords} onClick={placeOrder} data-testid="place-order-button">
            {placing ? "Placing order…" : `Pay ${formatRands(quote?.total ?? cart.subtotal)} (mock)`}
          </Button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Payment is mocked in this build.</p>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, className }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={className}>{value == null ? "—" : formatRands(value)}</span>
    </div>
  );
}
